/**
 * Hooks para o sistema de loading/autenticação
 */

import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useUserStore } from "@/_nucleo/store/_auth.store";
import { performTokenRefresh, clearRefreshState } from "@/_nucleo/api/refresh";
import { validateToken } from "./_api";
import type { LoadingState, LoadingStepId } from "./_types";
import { LOADING_STEPS } from "./_types";

export function useLoadingFlow() {
  const router = useRouter();
  const { auth, clear } = useUserStore();

  const [loadingState, setLoadingState] = useState<LoadingState>({
    currentStep: 0,
    progress: 0,
    isComplete: false,
    error: null,
  });

  const updateStep = useCallback((stepId: LoadingStepId) => {
    const step = LOADING_STEPS[stepId];
    setLoadingState((prev) => ({
      ...prev,
      currentStep: Object.keys(LOADING_STEPS).indexOf(stepId),
      progress: step.progress,
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setLoadingState((prev) => ({
      ...prev,
      error,
    }));
  }, []);

  const setComplete = useCallback(() => {
    setLoadingState((prev) => ({
      ...prev,
      isComplete: true,
      progress: 100,
    }));
  }, []);

  const startLoadingFlow = useCallback(async () => {
    try {
      updateStep("hydrating");
      await new Promise((resolve) => setTimeout(resolve, 500));
      updateStep("validating-token");
      const validationResult = await validateToken();

      if (
        !validationResult.success &&
        validationResult.needsRefresh &&
        auth?.refreshToken
      ) {
        updateStep("refreshing-token");
        try {
          await performTokenRefresh();

          updateStep("validating-again");
          const revalidationResult = await validateToken();

          if (!revalidationResult.success) {
            setError("Token inválido após refresh");
          }
        } catch (refreshError) {}
      }
      updateStep("redirecting");
      await new Promise((resolve) => setTimeout(resolve, 300));

      setComplete();
      router.push("/dashboard");
    } catch (error: any) {
      clear();
      clearRefreshState();
      router.push("/login");
    }
  }, [auth, router, clear, updateStep, setError, setComplete]);

  return {
    loadingState,
    startLoadingFlow,
  };
}
